import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandM, BrandS } from '@sorare/core/src/atoms/typography';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid';
import { isType } from '@sorare/core/src/lib/gql';
import { useCommonGameSet } from '@sorare/core/src/set';

import { SetCollectionTaskPreview } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/SetCollectionTaskPreview/index.tsx';
import { TaskCardPacks } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/TaskCardPacks/index.tsx';
import { TaskCardsOrCollections } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/TaskCardsOrCollections/index.tsx';
import { TaskRewards } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/TaskRewards/index.tsx';
import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import { TaskDetailsDescription } from './TaskDetailsDescription/index.tsx';
import type {
  SetCollectionTaskDetails_currentUser,
  SetCollectionTaskDetails_taskInterface,
} from './__generated__/index.graphql.ts';

const CompetitionBadge = styled(Trapezoid)<{
  $color: string;
}>`
  align-self: center;
  background: ${({ $color }) => $color};
  color: var(--c-white);
`;

const SectionChevron = styled(ChevronDownBold)<{ $expanded?: boolean }>`
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
  flex-shrink: 0;
`;

type Props = {
  task: SetCollectionTaskDetails_taskInterface;
  currentUser: Nullable<SetCollectionTaskDetails_currentUser>;
};

export const SetCollectionTaskDetails = ({ task, currentUser }: Props) => {
  const commonGameSet = useCommonGameSet();

  if (
    !isType(task, 'CardsCountTask') &&
    !isType(task, 'CompleteCollectionsCountTask')
  ) {
    return null;
  }

  const competitionColor = getTaskCompetitionColor(
    task,
    'var(--c-set-main-color)'
  );
  const competitionName =
    (isType(task, 'CardsCountTask') && task.competition?.displayName) ||
    (isType(task, 'CompleteCollectionsCountTask') &&
      task.competition?.displayName) ||
    commonGameSet.cardSet.displayName;
  const { description } = task;

  return (
    <Vertical gap={3}>
      <CompetitionBadge $color={competitionColor}>
        <BrandM>{competitionName}</BrandM>
      </CompetitionBadge>
      <BodyL className="text-center">{description}</BodyL>
      <SetCollectionTaskPreview
        task={task}
        overrideDescription={<TaskDetailsDescription task={task} />}
      />
      <Vertical style={{ display: 'none' }}>
        <SBHorizontal>
          <Horizontal>
            <Collection size={2} color="var(--c-set-main-color)" />
            <BrandS>
              <FormattedMessage defaultMessage="My collection" />
            </BrandS>
          </Horizontal>
          <SectionChevron $expanded={false} />
        </SBHorizontal>
      </Vertical>
      <TaskCardsOrCollections task={task} currentUser={currentUser} />
      <TaskRewards task={task} />
      <TaskCardPacks task={task} />
    </Vertical>
  );
};

SetCollectionTaskDetails.fragments = {
  taskInterface: gql`
    fragment SetCollectionTaskDetails_taskInterface on TaskInterface {
      id
      description
      ... on CardsCountTask {
        id
        competition {
          slug
          displayName
        }
      }
      ... on CompleteCollectionsCountTask {
        id
        competition {
          slug
          displayName
        }
      }
      ...SetCollectionTaskPreview_taskInterface
      ...TaskRewards_taskInterface
      ...TaskCardPacks_taskInterface
      ...getTaskCompetitionColor_taskInterface
      ...TaskDetailsDescription_taskInterface
      ...TaskCardsOrCollections_taskInterface
    }
    ${TaskCardsOrCollections.fragments.taskInterface}
    ${SetCollectionTaskPreview.fragments.taskInterface}
    ${TaskRewards.fragments.taskInterface}
    ${TaskCardPacks.fragments.taskInterface}
    ${getTaskCompetitionColor.fragments.taskInterface}
    ${TaskDetailsDescription.fragments.taskInterface}
  ` as TypedDocumentNode<SetCollectionTaskDetails_taskInterface>,
  currentUser: gql`
    fragment SetCollectionTaskDetails_currentUser on CurrentUser {
      slug
      ...TaskCardsOrCollections_currentUser
    }
    ${TaskCardsOrCollections.fragments.currentUser}
  ` as TypedDocumentNode<SetCollectionTaskDetails_currentUser>,
};
